import React, { FC, useContext, useEffect, useState } from "react";
import { IMembership } from "reducers/memberships";
import { MembershipsContext } from "contexts/memberships";
import { LocalizeText } from "components/localizer";
import { TDropdownItem } from "components/dropdown";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import TopBar from "components/tableComponents/topBar";
import { ProfileContext } from "contexts/profile";
import Banner from "components/banner";
import SearchField from "components/tableComponents/paginationSearchField";
import MembershipTable from "components/tableComponents/membershipTable";
import { usePaginationStore } from "utils/usePaginationStore";
import SubscriptionsContext from "contexts/subscriptions";
import { parseISO } from "date-fns";
import { dateIsPastOrToday } from "utils/helpers";

const MembershipsView: FC = () => {
    const { isFetchingEmailsCSV, getEmailsCSV, setMembership } = useContext(MembershipsContext);
    const { user, selectedOrganization } = useContext(ProfileContext);
    const pagination = usePaginationStore("memberships", undefined, {
        page_size: 20,
        default_page_size: 20,
        is_active: false,
        search: selectedOrganization.org_nr,
        ordering: "organization__name",
    });

    const { getBariumRequestMoreMembershipURL } = useContext(SubscriptionsContext);
    const [searchParams] = useSearchParams();
    const [forceTableLoading, setForceTableLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const paths = location.pathname.split("/").slice(1);
    const noSelectedMembership = paths[0] !== "memberships";

    const singleAccessUser = pagination.totalCount === 1;

    const selectMembership = (membership: IMembership, isSingleMembership: boolean): void => {
        if (searchParams.get("activity") === "apply") {
            setForceTableLoading(true);
            getBariumRequestMoreMembershipURL(membership.id)
                .catch(() => {
                    setForceTableLoading(false);
                })
                .then(() => {
                    setMembership(membership, isSingleMembership);
                    navigate(`${location.pathname}/${membership.id}`);
                });
        } else {
            setMembership(membership, isSingleMembership);
            navigate(`${location.pathname}/${membership.id}`);
        }
    };

    useEffect(() => {
        const isSingleMembership = async (): Promise<void> => {
            await pagination.getInitial();
            if (
                noSelectedMembership &&
                singleAccessUser &&
                pagination.results.length > 0 &&
                !dateIsPastOrToday(parseISO(pagination.results[0].end_date ?? ""))
            ) {
                selectMembership(pagination.results[0], true);
            }
        };
        isSingleMembership();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (
            noSelectedMembership &&
            singleAccessUser &&
            pagination.results.length === 1 &&
            !dateIsPastOrToday(parseISO(pagination.results[0].end_date ?? ""))
        ) {
            selectMembership(pagination.results[0], true);
        }

        if (
            !noSelectedMembership &&
            singleAccessUser &&
            pagination.results.length === 1 &&
            !dateIsPastOrToday(parseISO(pagination.results[0].end_date ?? ""))
        ) {
            navigate(`/staff-memberships/${pagination.results[0].id}/details`);
        }
        // eslint-disable-next-line
    }, [pagination.results, location]);

    const topMenuItems: TDropdownItem[] = [];
    if (user?.is_staff) {
        topMenuItems.push({
            onClick: () => getEmailsCSV(),
            label: <LocalizeText tag="downloadEmails" />,
            disabled: isFetchingEmailsCSV,
        });
    }

    return (
        <div data-testid="memberships-root" className="contentWrapper">
            <Banner
                title="memberships"
                subTitle="bannerMemberships"
                image={`${process.env.PUBLIC_URL}/assets/images/representatives/membershipAgreements.jpg`}
            />
            <TopBar
                searchField={
                    singleAccessUser && (
                        <SearchField pagination={pagination} tag="searchMemberships" validator="membership" />
                    )
                }
                menuItems={topMenuItems}
            />
            <MembershipTable
                pagination={pagination}
                onClick={
                    noSelectedMembership
                        ? (membership) => selectMembership(membership, false)
                        : (membership) => navigate(`/staff-memberships/${membership.id}/details`)
                }
                forceLoadingBehaviour={forceTableLoading}
            />
        </div>
    );
};

export default MembershipsView;
